import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { glassCardStatic } from '@/utils/admin-glass-styles';

const RefundReconciler: React.FC = () => {
  const [bookingId, setBookingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const { toast } = useToast();

  const handleReconcile = async () => {
    if (!bookingId) {
      toast({ variant: 'destructive', title: 'Booking ID is required' });
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('reconcile-refund', {
        body: { bookingId }
      });
      if (error) throw error;
      toast({ title: 'Reconciliation complete', description: data?.message || 'Status updated if needed.' });
    } catch (err: any) {
      console.error(err);
      toast({ variant: 'destructive', title: 'Failed to reconcile', description: err?.message || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchReconcile = async () => {
    try {
      setBatchLoading(true);
      const { data, error } = await supabase.functions.invoke('reconcile-refunds-batch', { body: { limit: 100 } });
      if (error) throw error;
      toast({
        title: 'Batch reconciliation complete',
        description: `Checked ${data?.checked ?? 0}, updated ${data?.updated ?? 0}.`
      });
    } catch (err: any) {
      console.error(err);
      toast({ variant: 'destructive', title: 'Batch reconcile failed', description: err?.message || 'Please try again.' });
    } finally {
      setBatchLoading(false);
    }
  };

  return (
    <Card className={glassCardStatic}>
      <CardHeader>
        <CardTitle>Refund Reconciliation</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-3">
          <Input
            placeholder="Enter Booking ID (UUID)"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
          />
          <Button onClick={handleReconcile} disabled={loading}>
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reconciling...</> : <><RefreshCcw className="h-4 w-4 mr-2" /> Reconcile Refund</>}
          </Button>
          <Button variant="secondary" onClick={handleBatchReconcile} disabled={batchLoading}>
            {batchLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Batch reconciling...</> : <><RefreshCcw className="h-4 w-4 mr-2" /> Batch Reconcile</>}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-2">Use this tool when a Stripe refund completed but the booking still shows "Refund Processing".</p>
      </CardContent>
    </Card>
  );
};

export default RefundReconciler;
